import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Share2, MessageCircle, Unlock, X } from "lucide-react";
import { useGrowth, inviteMsg, loadGrowth } from "@/lib/growth";
import { openShare, getShareUrl } from "@/lib/share";

const shareInvite = () => openShare({ kind: "invite", title: "Freunde einladen", msg: inviteMsg });
const whatsapp = async () => {
  const w = window.open("", "_blank");
  const url = await getShareUrl({ kind: "invite" }).catch(() => `${window.location.origin}/registrieren`);
  const href = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${inviteMsg} ${url}`)}`;
  if (w) w.location.href = href; else window.location.href = href;
};

function LockCard({ g, gate, dark, onClose }) {
  const pct = Math.min(100, Math.round((g.count / Math.max(1, gate.need)) * 100));
  const miss = gate.need - g.count;
  return (
    <div className={`relative overflow-hidden rounded-[26px] p-5 ${dark ? "bg-white/10 ring-1 ring-white/20 backdrop-blur-xl text-white" : "menu-hero text-white shadow-xl shadow-rose-200/60"}`} data-testid={`invite-lock-${gate.id}`}>
      {onClose && <button onClick={onClose} aria-label="Schließen" data-testid="invite-gate-close" className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 flex items-center justify-center"><X className="w-4 h-4" /></button>}
      <div className="flex items-start gap-3">
        <span className="w-12 h-12 shrink-0 rounded-2xl bg-white/20 ring-1 ring-white/30 flex items-center justify-center"><Lock className="w-6 h-6" /></span>
        <div className="min-w-0 pr-6">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-white/80">Noch gesperrt</p>
          <p className="font-display font-extrabold text-lg leading-snug">{gate.label} ab {gate.need} {gate.need === 1 ? "Freund" : "Freunden"}</p>
          <p className="text-sm text-white/85 mt-0.5" data-testid={`invite-lock-${gate.id}-missing`}>Dir {miss === 1 ? "fehlt" : "fehlen"} noch <b>{miss}</b> – jede Anmeldung über deinen Link zählt sofort.</p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-2.5 rounded-full bg-white/20 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-amber-200 to-white" style={{ width: `${Math.max(5, pct)}%` }} /></div>
        <span className="text-sm font-extrabold tabular-nums">{g.count}/{gate.need}</span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button onClick={whatsapp} data-testid={`invite-lock-${gate.id}-whatsapp`} className="h-11 rounded-full bg-white text-emerald-600 font-extrabold text-sm inline-flex items-center justify-center gap-2 active:scale-95 transition-transform"><MessageCircle className="w-4 h-4" /> WhatsApp</button>
        <button onClick={shareInvite} data-testid={`invite-lock-${gate.id}-share`} className="h-11 rounded-full bg-white/15 ring-1 ring-white/40 font-extrabold text-sm inline-flex items-center justify-center gap-2 active:scale-95 transition-transform"><Share2 className="w-4 h-4" /> Teilen</button>
      </div>
    </div>
  );
}

export function InviteLock({ gate: id, dark }) {
  const g = useGrowth();
  const gate = g?.gates?.find((x) => x.id === id);
  if (!g || !gate || gate.unlocked) return null;
  return <LockCard g={g} gate={gate} dark={dark} />;
}

export function GatesOverview() {
  const g = useGrowth();
  if (!g?.gates) return null;
  return (
    <section className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 shadow-sm" data-testid="gates-overview">
      <h3 className="font-bold text-lg flex items-center gap-2"><Unlock className="w-5 h-5 text-rose-500" /> Das schaltest du mit Freunden frei</h3>
      <ul className="mt-4 space-y-2">
        {g.gates.map((x) => (
          <li key={x.id} data-testid={`gate-${x.id}`} data-unlocked={x.unlocked || undefined} className={`flex items-center gap-3 rounded-2xl px-4 py-3 border ${x.unlocked ? "border-emerald-200 bg-emerald-50" : "border-pink-100"}`}>
            <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${x.unlocked ? "bg-emerald-500 text-white" : "bg-slate-800 text-white"}`}>{x.unlocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}</span>
            <span className="flex-1 min-w-0 font-semibold text-slate-800">{x.label}</span>
            <span className={`text-xs font-extrabold ${x.unlocked ? "text-emerald-600" : "text-slate-500"}`}>{x.unlocked ? "Freigeschaltet" : `ab ${x.need} ${x.need === 1 ? "Freund" : "Freunden"} · ${g.count}/${x.need}`}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function InviteGateHost() {
  const [gateId, setGateId] = useState(null);
  const [g, setG] = useState(null);
  useEffect(() => {
    const on = (e) => {
      loadGrowth().then((d) => {
        const gate = d.gates?.find((x) => !x.unlocked && e.detail.includes(x.label)) || d.gates?.find((x) => !x.unlocked);
        if (gate) { setG(d); setGateId(gate.id); }
      }).catch(() => {});
    };
    window.addEventListener("invite-gate", on);
    return () => window.removeEventListener("invite-gate", on);
  }, []);
  const gate = g?.gates?.find((x) => x.id === gateId);
  if (!gate) return null;
  const close = () => setGateId(null);
  return (
    <div style={{ pointerEvents: "auto" }} className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={close} data-testid="invite-gate-dialog">
      <div className="w-full max-w-md fade-rise" onClick={(e) => e.stopPropagation()}>
        <LockCard g={g} gate={gate} onClose={close} />
        <Link to="/einladen" onClick={close} data-testid="invite-gate-more" className="mt-3 block text-center text-sm font-bold text-white/90 underline underline-offset-4">Alle Einlade-Vorteile ansehen</Link>
      </div>
    </div>
  );
}
